import {
  Filter,
  type SelectItem,
  useSelectQueryGate,
} from '@vrplatform/components';
import {
  type gqlV2,
  useDashboard,
  useInfiniteQuery,
  useQuery,
  useTeam,
} from '@vrplatform/data';
import { parseAsString, useQueryStates } from '@vrplatform/query-params';
import { Group } from '@mantine/core';
import { VRP_TENANT_ID } from '@vrplatform/graphql';
import {
  type TenantStatus,
  whereConnectionStatusDefault,
  whereTenants,
} from '@vrplatform/ui-common';
import { useMemo, useState } from 'react';

export const usePartnerConnectionsTableFilter = () => {
  const [filter, setFilter] = useQueryStates({
    search: parseAsString,
    status: parseAsString,
    appId: parseAsString,
    tenantId: parseAsString,
  });

  return {
    filter,
    setFilter,
    reset: () => setFilter(null),
  };
};

export const PartnerConnectionsTableFilter = ({
  hide,
}: {
  hide?: ('team' | 'app' | 'status')[];
}) => {
  return (
    <Group>
      <Search />
      {!hide?.includes('status') && <Status />}
      {!hide?.includes('team') && <Teams />}
      {!hide?.includes('app') && <App />}
    </Group>
  );
};

const Search = () => {
  const { filter, setFilter } = usePartnerConnectionsTableFilter();

  return (
    <Filter.Search
      value={filter.search || ''}
      setValue={(v) => setFilter({ search: v || null })}
    />
  );
};

const Status = () => {
  const { filter, setFilter } = usePartnerConnectionsTableFilter();

  const options = useMemo<SelectItem<TenantStatus | 'onboarding'>[]>(
    () => [
      {
        label: 'Onboarding',
        value: 'onboarding',
      },
      {
        label: 'Active',
        value: 'active',
      },
      {
        label: 'Suspended',
        value: 'inactive',
      },
    ],
    []
  );

  const status = options.find((o) => o.value === filter.status) || null;

  return (
    <Filter.Select
      type="single"
      value={status}
      data={{
        options,
      }}
      label="Status"
      setValue={(v) => {
        setFilter({ status: v?.value || null });
      }}
    />
  );
};

const App = () => {
  const { filter, setFilter } = usePartnerConnectionsTableFilter();
  const gate = useSelectQueryGate({ value: filter.appId });

  const [search, setSearch] = useState('');
  const [{ id: teamId }] = useTeam();

  const appId = filter.appId;

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: gqlV2.app_bool_exp = {
        connections: {
          tenant:
            args.teamId === VRP_TENANT_ID
              ? undefined
              : {
                  partnerId: { _eq: args.teamId },
                },
          status: whereConnectionStatusDefault,
          appId: { _neq: 'finalytic' },
        },
        name: args.search ? { _ilike: `%${args.search}%` } : undefined,
      };

      const list = q
        .app({
          where,
          order_by: [{ name: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((app) => ({
          label: app.name || '',
          value: app.id || '',
        }));

      const aggregate = q.appAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !teamId || !gate.shouldFetchAny,
      queryKey: 'connections',
      variables: {
        teamId,
        search: search.trim(),
      },
    }
  );

  const options = queryData.data?.pages.flatMap((page) => page.list) || [];

  const value = gate.hasValue
    ? options.find((option) => option.value === appId) || null
    : null;

  return (
    <Filter.Select
      type="single"
      value={value}
      infiniteData={{ ...queryData, setSearch }}
      label="App"
      onOpenChange={gate.onOpenChange}
      setValue={(v) => {
        setFilter({ appId: v?.value || null });
      }}
    />
  );
};

const Teams = () => {
  const { filter, setFilter } = usePartnerConnectionsTableFilter();

  const [dashboard] = useDashboard();
  const [{ id: partnerId }] = useTeam();
  const gate = useSelectQueryGate({ value: filter.tenantId });

  const [search, setSearch] = useState('');

  const tenantId = filter.tenantId;

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where: gqlV2.tenant_bool_exp = whereTenants({
        partnerId: args.partnerId,
        search: args.search,
        dashboard: args.dashboard,
      });

      const list = q
        .tenant({
          where,
          order_by: [{ name: 'asc_nulls_last' }],
          limit,
          offset,
        })
        .map<SelectItem>((tenant) => ({
          label: tenant.name || '',
          value: tenant.id || '',
        }));

      const aggregate = q.tenantAggregate({ where }).aggregate?.count() || 0;

      return {
        list,
        aggregate,
      };
    },
    {
      skip: !partnerId || !gate.shouldFetchOptions,
      queryKey: 'connections',
      variables: {
        partnerId,
        search: search.trim(),
        tenantId: filter.tenantId,
        dashboard,
      },
    }
  );

  const { data } = useQuery(
    (q, args) => {
      if (!args.tenantId) return null;

      return (
        q
          .tenant({
            where: {
              id: { _eq: args.tenantId },
            },
            limit: 1,
          })
          .map((x) => ({
            value: x.id,
            label: x.name!,
          }))[0] || null
      );
    },
    {
      skip: !tenantId || !gate.shouldFetchValue,
      variables: {
        tenantId,
      },
    }
  );

  const value = gate.hasValue ? data || null : null;

  return (
    <Filter.Select
      type="single"
      value={value}
      infiniteData={{ ...queryData, setSearch }}
      label="Team"
      onOpenChange={gate.onOpenChange}
      setValue={(v) => {
        setFilter({ tenantId: v?.value || null });
      }}
    />
  );
};
